import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Tooltip, Stack, Paper, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../../context/AuthContext';

const MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const MonthlyCategoryStrips = ({ breakdown = [], preferredCurrency, colorMap: externalColorMap, onCategoryClick }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { formatCurrency } = useAuth();
    const [hoveredCategory, setHoveredCategory] = useState(null);

    // Calculate annual spending per category to determine global order and coloring
    const categoryStats = useMemo(() => {
        const categoryColors = theme.palette.chartColors || [
            theme.palette.primary.main,
            '#2563EB',
            '#7C3AED',
            '#D97706',
            '#E11D48',
            '#0891B2',
            '#C026D3',
            '#EA580C',
            '#0D9488',
            '#64748B'
        ];

        const annualTotals = {};
        if (breakdown && Array.isArray(breakdown)) {
            breakdown.forEach(month => {
                if (month && month.categories) {
                    month.categories.forEach(cat => {
                        annualTotals[cat.name] = (annualTotals[cat.name] || 0) + (cat.amount || 0);
                    });
                }
            });
        }

        // Sort categories by total annual spending (descending)
        const sortedCategories = Object.entries(annualTotals)
            .sort((a, b) => b[1] - a[1]) // Sort by amount descending
            .map(([name]) => name);

        const internalColorMap = {};
        let nonOtherIndex = 0;
        sortedCategories.forEach((name) => {
            if (name === 'Other') {
                internalColorMap[name] = '#64748B';
            } else {
                internalColorMap[name] = categoryColors[nonOtherIndex % categoryColors.length];
                nonOtherIndex++;
            }
        });

        const finalColorMap = externalColorMap ? { ...internalColorMap, ...externalColorMap } : internalColorMap;

        return { colorMap: finalColorMap, sortedCategories };
    }, [breakdown, theme, externalColorMap]);

    const { colorMap: categoryColorMap, sortedCategories: globalCategoryOrder } = categoryStats;

    const maxMonthlySpending = useMemo(() => {
        if (!breakdown || breakdown.length === 0) return 0;
        return Math.max(...breakdown.map(m => m.total || 0), 0);
    }, [breakdown]);

    if (!breakdown || breakdown.length === 0) {
        return (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid rgba(193, 200, 194, 0.25)' }}>
                <Typography color="text.secondary">{t('No data available')}</Typography>
            </Paper>
        );
    }

    return (
        <Paper elevation={0} sx={{
            width: '100%',
            p: 4,
            bgcolor: 'background.paper',
            border: '1px solid rgba(193, 200, 194, 0.25)',
            boxShadow: '0 12px 32px rgba(28, 28, 24, 0.04)'
        }}>
            <Typography variant="h6" sx={{ 
                mb: 4, 
                fontWeight: 700, 
                color: 'text.primary',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '0.9rem'
            }}>
                {t('Monthly Category Spending')}
            </Typography>

            <Stack spacing={3}>
                {breakdown.map((monthData, idx) => {
                    const monthName = MONTH_NAMES[(monthData.month || 1) - 1];
                    const total = monthData.total || 0;
                    const isZero = total === 0;

                    let relativeWidth = 0;
                    if (!isZero && maxMonthlySpending > 0) {
                        relativeWidth = (total / maxMonthlySpending) * 100;
                    }

                    const categories = monthData.categories || [];
                    const sortedMonthCategories = [...categories].sort((a, b) => {
                        return globalCategoryOrder.indexOf(a.name) - globalCategoryOrder.indexOf(b.name);
                    });

                    return (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: 50, flexShrink: 0 }}>
                                <Typography variant="caption" sx={{ 
                                    fontWeight: 700, 
                                    color: 'text.secondary', 
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase'
                                }}>
                                    {t(monthName)}
                                </Typography>
                            </Box>

                            <Box sx={{
                                flexGrow: 1,
                                maxWidth: '100%',
                                height: 32,
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <Box sx={{
                                    width: `${relativeWidth}%`,
                                    minWidth: isZero ? 60 : 2,
                                    height: '100%',
                                    display: 'flex',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    bgcolor: 'surface-container-low',
                                    transition: 'all 0.3s ease',
                                }}>
                                    {isZero ? (
                                        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                                                —
                                            </Typography>
                                        </Box>
                                    ) : (
                                        sortedMonthCategories.map((cat, catIdx) => {
                                            const width = total > 0 ? (cat.amount / total) * 100 : 0;
                                            if (width < 0.1) return null;

                                            const isHighlighted = hoveredCategory === cat.name;

                                            const topCategories = sortedMonthCategories.filter(c => c.name !== 'Other').map(c => c.name);
                                            const tooltipTitle = onCategoryClick
                                                ? `${t(cat.name)}: ${formatCurrency(cat.amount)} (${Math.round(width)}%) • ${t('Click to view transactions')}`
                                                : `${t(cat.name)}: ${formatCurrency(cat.amount)} (${Math.round(width)}%)`;

                                            return (
                                                <Tooltip
                                                    key={cat.name}
                                                    title={tooltipTitle}
                                                    arrow
                                                    placement="top"
                                                    enterTouchDelay={0}
                                                >
                                                    <Box
                                                        id={`category-strip-${monthData.month}-${cat.name.replace(/\s+/g, '-').toLowerCase()}`}
                                                        data-testid={`category-strip-${monthData.month}-${cat.name.replace(/\s+/g, '-').toLowerCase()}`}
                                                        onClick={() => {
                                                            if (onCategoryClick) {
                                                                onCategoryClick({
                                                                    month: monthData.month,
                                                                    monthName,
                                                                    categoryName: cat.name,
                                                                    amount: cat.amount,
                                                                    topCategories
                                                                });
                                                            }
                                                        }}
                                                        onMouseEnter={() => setHoveredCategory(cat.name)}
                                                        onMouseLeave={() => setHoveredCategory(null)}
                                                        sx={{
                                                            width: `${width}%`,
                                                            height: '100%',
                                                            bgcolor: categoryColorMap[cat.name] || '#cbd5e1',
                                                            cursor: onCategoryClick ? 'pointer' : 'default',
                                                            borderRight: '1px solid rgba(255,255,255,0.05)',
                                                            opacity: !hoveredCategory || isHighlighted ? 1 : 0.3,
                                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            '&:hover': onCategoryClick ? {
                                                                filter: 'brightness(1.15)',
                                                                transform: 'scaleY(1.08)',
                                                                zIndex: 1
                                                            } : {}
                                                        }}
                                                    />
                                                </Tooltip>
                                            );
                                        })
                                    )}
                                </Box>
                            </Box>

                            <Box sx={{ width: 80, textAlign: 'right', ml: 2, flexShrink: 0 }}>
                                <Typography variant="caption" sx={{ 
                                    fontWeight: 700, 
                                    fontFamily: '"Manrope", sans-serif',
                                    color: isZero ? 'text.disabled' : 'text.primary', 
                                    fontSize: '0.8rem' 
                                }}>
                                    {formatCurrency(monthData.total)}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Stack>

            {/* Legend */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 5, gap: 1.5, pt: 3, borderTop: '1px solid rgba(193, 200, 194, 0.1)' }}>
                {globalCategoryOrder.map((name, i) => {
                    const color = categoryColorMap[name];
                    const isHighlighted = hoveredCategory === name;

                    return (
                        <Box
                            key={name}
                            onMouseEnter={() => setHoveredCategory(name)}
                            onMouseLeave={() => setHoveredCategory(null)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'default',
                                padding: '6px 12px',
                                borderRadius: 1,
                                bgcolor: isHighlighted ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                                transition: 'all 0.2s ease',
                                opacity: !hoveredCategory || isHighlighted ? 1 : 0.4,
                            }}
                        >
                            <Box sx={{
                                width: 8,
                                height: 8,
                                borderRadius: 1,
                                bgcolor: color,
                                mr: 1.5,
                            }} />
                            <Typography variant="caption" sx={{
                                color: 'text.primary',
                                fontWeight: isHighlighted ? 700 : 500,
                                fontSize: '0.75rem',
                                letterSpacing: '0.02em'
                            }}>
                                {t(name)}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
};

export default React.memo(MonthlyCategoryStrips);
